import React from 'react';
import { ListPlus, Lock } from 'lucide-react';
import { Casilla, MoneyInput, fmtMoney } from './f350Utils';

const Cell = ({ num, value, fuente, onChange, onDetalle, detalleCount }) => {
  if (!num) return <td className="bg-slate-100/70 border border-slate-200" />;
  const locked = fuente === 'detalle' || fuente === 'hoja2';
  return (
    <td className="border border-slate-200 p-1 align-middle">
      <div className="flex items-center gap-1">
        <Casilla num={num} />
        {locked ? (
          <div className="h-8 flex-1 rounded-md border border-slate-200 bg-slate-50 px-2 text-right text-sm tabular-nums leading-8 text-slate-700" title={fuente === 'hoja2' ? 'Calculado desde la Hoja 2 (pagos al exterior)' : 'Suma del detalle de terceros'} data-testid={`f350-cas-${num}`}>{fmtMoney(value)}</div>
        ) : (
          <MoneyInput value={value} onChange={onChange} testId={`f350-cas-${num}`} />
        )}
        {onDetalle && (
          <button type="button" onClick={onDetalle} title={locked && fuente === 'hoja2' ? 'Se calcula desde la Hoja 2' : 'Detalle por terceros'} disabled={fuente === 'hoja2'}
            className={`shrink-0 rounded-md p-1 ${detalleCount ? 'text-[#1F3864] bg-blue-50' : 'text-slate-400 hover:text-[#1F3864]'} disabled:opacity-40`} data-testid={`f350-detalle-${num}`}>
            {fuente === 'hoja2' ? <Lock className="w-3.5 h-3.5" /> : <ListPlus className="w-3.5 h-3.5" />}
          </button>
        )}
      </div>
    </td>
  );
};

export const F350Conceptos = ({ schema, data, computed, onCasilla, onDetalle }) => {
  const { conceptos, secciones, otras, totales } = schema;
  const cas = computed?.casillas || {};
  const fuente = computed?.fuente || {};
  const val = (num) => (fuente[num] === 'manual' ? data.casillas?.[num] ?? 0 : cas[num] ?? 0);
  let seccionActual = null;

  const TotalRow = ({ num, bold = true }) => (
    <tr className={bold ? 'bg-slate-50' : ''}>
      <td colSpan={4} className={`border border-slate-200 px-2 py-1.5 text-sm ${bold ? 'font-semibold text-slate-900' : 'text-slate-700'}`}>{totales[num]}</td>
      <td className="border border-slate-200 p-1">
        <div className="flex items-center gap-1"><Casilla num={num} /><div className={`h-8 flex-1 rounded-md px-2 text-right text-sm tabular-nums leading-8 ${bold ? 'bg-[#1F3864] text-white font-semibold' : 'bg-slate-50 text-slate-800'}`} data-testid={`f350-cas-${num}`}>{fmtMoney(cas[num])}</div></div>
      </td>
    </tr>
  );
  const OtraRow = ({ o }) => (
    <tr>
      <td colSpan={4} className="border border-slate-200 px-2 py-1 text-sm text-slate-700">{o.nombre}</td>
      <td className="border border-slate-200 p-1"><div className="flex items-center gap-1"><Casilla num={o.casilla} /><MoneyInput value={data.casillas?.[o.casilla] ?? 0} onChange={(v) => onCasilla(o.casilla, v)} testId={`f350-cas-${o.casilla}`} /></div></td>
    </tr>
  );

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="f350-conceptos">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <h3 className="text-sm font-semibold text-slate-900">Retenciones practicadas</h3>
        <p className="text-xs text-slate-500">Digita los valores o usa <ListPlus className="inline w-3.5 h-3.5" /> para detallar por terceros (la casilla se calcula sola). Los valores se aproximan a miles.</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm" style={{ minWidth: 980 }}>
          <thead>
            <tr className="bg-slate-50 text-xs text-slate-600">
              <th rowSpan={2} className="border border-slate-200 px-2 py-1.5 text-left font-semibold w-[32%]">Concepto</th>
              <th colSpan={2} className="border border-slate-200 px-2 py-1 font-semibold text-[#1F3864]">A personas jurídicas</th>
              <th colSpan={2} className="border border-slate-200 px-2 py-1 font-semibold text-[#1F3864]">A personas naturales</th>
            </tr>
            <tr className="bg-slate-50 text-[11px] text-slate-500">
              <th className="border border-slate-200 px-2 py-1 font-medium">Base sujeta a retención</th><th className="border border-slate-200 px-2 py-1 font-medium">Retención a título de renta</th>
              <th className="border border-slate-200 px-2 py-1 font-medium">Base sujeta a retención</th><th className="border border-slate-200 px-2 py-1 font-medium">Retención a título de renta</th>
            </tr>
          </thead>
          <tbody>
            {conceptos.map((c) => {
              const header = c.seccion !== seccionActual && c.seccion !== 'renta' ? <tr key={`h-${c.seccion}`}><td colSpan={5} className="border border-slate-200 bg-slate-100 px-2 py-1 text-xs font-semibold text-[#1F3864]">{secciones[c.seccion]}</td></tr> : null;
              seccionActual = c.seccion;
              return (
                <React.Fragment key={c.key}>
                  {header}
                  <tr className="hover:bg-slate-50/60" data-testid={`f350-row-${c.key}`}>
                    <td className="border border-slate-200 px-2 py-1 text-sm text-slate-800">{c.nombre}</td>
                    {['pj', 'pn'].map((tipo) => {
                      const pair = c[tipo];
                      const lines = data.detalles?.[`${c.key}_${tipo}`]?.length || 0;
                      if (!pair) return <React.Fragment key={tipo}><Cell /><Cell /></React.Fragment>;
                      return (
                        <React.Fragment key={tipo}>
                          <Cell num={pair[0]} value={val(pair[0])} fuente={fuente[pair[0]]} onChange={(v) => onCasilla(pair[0], v)} />
                          <Cell num={pair[1]} value={val(pair[1])} fuente={fuente[pair[1]]} onChange={(v) => onCasilla(pair[1], v)} onDetalle={() => onDetalle(c, tipo)} detalleCount={lines} />
                        </React.Fragment>
                      );
                    })}
                  </tr>
                </React.Fragment>
              );
            })}
            <OtraRow o={otras[0]} />
            <TotalRow num={130} />
            <tr><td colSpan={5} className="border border-slate-200 bg-slate-100 px-2 py-1 text-xs font-semibold text-[#1F3864]">Retenciones practicadas por otros impuestos</td></tr>
            {otras.slice(1, 4).map((o) => <OtraRow key={o.casilla} o={o} />)}
            <TotalRow num={134} />
            <OtraRow o={otras[4]} />
            <TotalRow num={136} />
            <TotalRow num={137} bold={false} />
            <TotalRow num={138} />
            <TotalRow num={980} />
          </tbody>
        </table>
      </div>
    </div>
  );
};
